import React, { useState } from 'react';
import { Image, Tooltip, message } from 'antd';
import {
  CameraOutlined,
  CheckCircleFilled,
  CheckOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  EnvironmentOutlined,
  MobileOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  DeviceIdentity,
  DeviceInfo,
  DeviceThumbWrapper,
  HeroCanvas,
  HeroGrid,
  ProfileStack,
  SpecPill,
  ValuationHighlight,
} from './HistoryDetail.styles';

const HistoryDetailHeader = ({ app }) => {
  const [copiedKey, setCopiedKey] = useState(null);

  const isAccepted = String(app?.status || '').toLowerCase().includes('accepted');
  const isRejected = String(app?.status || '').toLowerCase().includes('rejected');
  const images = Array.isArray(app?.images) ? app.images : [];

  const handleCopy = (text, key, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    message.success(`${label} copied to clipboard!`);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const customerInitials = (app?.customerName || 'CU')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const formattedQuote = Number(app?.quotedAmount || app?.bidAmount || 0).toLocaleString('en-IN');
  const formattedHighest = Number(app?.highestBid || 0).toLocaleString('en-IN');

  return (
    <HeroCanvas>
      <HeroGrid>
        {/* DEVICE IDENTITY */}
        <DeviceIdentity>
          <DeviceThumbWrapper>
            {images.length > 0 ? (
              <Image.PreviewGroup>
                <Image src={images[0]} alt={app.deviceModel || 'Device'} preview={{ mask: <CameraOutlined /> }} />
                {images.length > 1 && <span className="photo-counter">{images.length} Photos</span>}
              </Image.PreviewGroup>
            ) : (
              <MobileOutlined style={{ fontSize: 36, color: '#94a3b8' }} />
            )}
          </DeviceThumbWrapper>

          <DeviceInfo>
            <div className="brand-chip">Exchange Device Inspection</div>
            <div className="model-name">{app.deviceModel || 'Smartphone'}</div>
            <div className="badges-row">
              {app.condition && app.condition !== '-' && (
                <SpecPill style={{ background: '#ecfdf5', color: '#059669', fontWeight: 700 }}>
                  <SafetyCertificateOutlined /> {app.condition}
                </SpecPill>
              )}
              {app.imeiNumber && (
                <Tooltip title="Click to copy IMEI">
                  <SpecPill
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleCopy(app.imeiNumber, 'imei', 'IMEI Number')}
                  >
                    IMEI: {app.imeiNumber}
                    {copiedKey === 'imei' ? <CheckOutlined style={{ color: '#10b981' }} /> : <CopyOutlined />}
                  </SpecPill>
                </Tooltip>
              )}
              {app.storeCode && <SpecPill>Store: {app.storeCode}</SpecPill>}
            </div>
          </DeviceInfo>
        </DeviceIdentity>

        {/* CUSTOMER & STORE PROFILE */}
        <ProfileStack>
          <div className="profile-row">
            <div className="profile-avatar">{customerInitials}</div>
            <div className="profile-info">
              <div className="label">Customer Profile</div>
              <div className="name">{app.customerName || 'Valued Customer'}</div>
              {app.customerMobile ? (
                <a href={`tel:${app.customerMobile}`} className="meta-link">
                  <PhoneOutlined /> {app.customerMobile}
                </a>
              ) : (
                <span style={{ fontSize: 11, color: '#94a3b8' }}>No mobile registered</span>
              )}
            </div>
          </div>

          <div className="profile-row">
            <div className="profile-avatar" style={{ background: '#f0fdf4', color: '#16a34a' }}>
              <ShopOutlined />
            </div>
            <div className="profile-info">
              <div className="label">Store Branch</div>
              <div className="name">{app.storeName || 'Branch Details'}</div>
              {app.storeAddress ? (
                <span style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <EnvironmentOutlined /> {app.storeAddress}
                </span>
              ) : null}
            </div>
          </div>
        </ProfileStack>

        {/* VALUATION APEX */}
        <ValuationHighlight $isAccepted={isAccepted}>
          <div className="apex-label">
            {isAccepted ? (
              <>
                <TrophyOutlined style={{ color: '#fbbf24' }} /> Accepted Winning Quote
              </>
            ) : isRejected ? (
              <>
                <ClockCircleOutlined style={{ color: '#fca5a5' }} /> Offer Status: {app.status}
              </>
            ) : (
              <>
                <ThunderboltOutlined style={{ color: '#818cf8' }} /> Your Quoted Offer
              </>
            )}
          </div>
          <div className="apex-amount">
            ₹{formattedQuote}
          </div>
          <div className="apex-meta">
            {isAccepted ? (
              <>
                <CheckCircleFilled style={{ color: '#34d399' }} /> Finalized Winning Vendor Offer
              </>
            ) : Number(app?.highestBid) > 0 ? (
              `Highest Live Offer: ₹${formattedHighest}`
            ) : (
              'Awaiting selection by customer'
            )}
          </div>
        </ValuationHighlight>
      </HeroGrid>
    </HeroCanvas>
  );
};

export default HistoryDetailHeader;
